import { useEffect, useState } from "react";
import { Activity, ArrowDownRight, ArrowUpRight, Boxes, CalendarDays, CircleDollarSign, MapPinned, Package, RotateCw, ShoppingBag, Store, Users } from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../api/axios";
import AppShell from "../components/AppShell";
import DataTable from "../components/DataTable";
import DistrictNetworkDetail from "../components/DistrictNetworkDetail";
import { useAuth } from "../context/AuthContext";
import { FALLBACK_PERMISSIONS } from "../constants/permissions";

const CHART_COLORS = ["#246b8e", "#2a9d8f", "#e5a33d", "#d96b55", "#6c7b95", "#7aa27a", "#9b7eaa", "#c47c4b"];
const STATUS_COLORS = {
  pending: "#e5a33d",
  confirmed: "#2a9d8f",
  processing: "#246b8e",
  ongoing: "#6c7b95",
  dispatched: "#52845d",
  cancelled: "#d96b55",
};
const PERIODS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "6m", label: "6 months" },
  { key: "12m", label: "12 months" },
];
const EMPTY_COUNTS = {
  distributors: { total: 0, active: 0, blocked: 0 },
  states: 0,
  districts: 0,
  salesPeople: { total: 0, active: 0 },
  shops: { total: 0, active: 0 },
  products: { total: 0, active: 0, categories: 0, subcategories: 0 },
  inventory: { variants: 0, stockUnits: 0, lowStockVariants: 0, outOfStockVariants: 0 },
  orders: { total: 0, distributor: 0, shop: 0, pending: 0, processing: 0, dispatched: 0, cancelled: 0 },
};

const formatMoney = (value) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
}).format(Number(value) || 0);

const formatCompactMoney = (value) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  notation: "compact",
  maximumFractionDigits: 1,
}).format(Number(value) || 0);

const formatDate = (value) => value ? new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "-";
const statusLabel = (value = "") => value.charAt(0).toUpperCase() + value.slice(1);

export default function Dashboard() {
  const { admin, isSuperAdmin } = useAuth();
  const [overview, setOverview] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [selectedDistrictId, setSelectedDistrictId] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    api.get("/dashboard/overview", { params: { range: period } })
      .then(({ data }) => { if (active) setOverview(data.overview); })
      .catch((requestError) => {
        if (active) setError(requestError.response?.data?.message || "Could not load the business overview.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isSuperAdmin, period, refreshKey]);

  useEffect(() => {
    const districts = overview?.districtPerformance || [];
    if (!districts.length) return;
    setSelectedDistrictId((current) => districts.some((district) => district.id === current)
      ? current
      : (districts.find((district) => district.orders > 0) || districts[0]).id);
  }, [overview]);

  if (!isSuperAdmin) return <PermissionOverview admin={admin} />;

  const counts = overview?.counts || EMPTY_COUNTS;
  const orderValue = overview?.orderValue || { distributor: 0, shop: 0, total: 0 };
  const comparison = overview?.comparison || { orders: 0, orderValue: { total: 0 } };
  const rangeLabel = overview?.range?.label || "Last 30 days";
  const comparisonLabel = overview?.range?.comparisonLabel || "previous 30 days";
  const hasOrders = counts.orders.total > 0;
  const stateData = (overview?.distributorsByState || []).filter((state) => state.distributors > 0);
  const orderingDistricts = (overview?.districtPerformance || []).filter((district) => district.orderValue > 0).slice(0, 8);
  const districtOptions = overview?.districtPerformance || [];

  return (
    <AppShell>
      <main className="firm-dashboard">
        <header className="dashboard-header">
          <div>
            <div className="dashboard-eyebrow"><Activity size={14} aria-hidden="true" /> OWNER OVERVIEW</div>
            <h1>Business overview</h1>
            <p>Order activity for {rangeLabel.toLowerCase()}, with network and inventory snapshots.</p>
          </div>
          <div className="dashboard-header-actions">
            <div className="dashboard-period" role="group" aria-label="Filter order data by date range">
              {PERIODS.map((item) => <button key={item.key} type="button" aria-pressed={period === item.key} className={period === item.key ? "is-selected" : ""} onClick={() => setPeriod(item.key)}>{item.label}</button>)}
            </div>
            <button className="dashboard-refresh" type="button" title="Refresh overview" aria-label="Refresh overview" disabled={loading} onClick={() => setRefreshKey((current) => current + 1)}>
              <RotateCw size={16} className={loading ? "is-spinning" : ""} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="dashboard-data-note"><CalendarDays size={15} aria-hidden="true" /> Order data: {rangeLabel} vs {comparisonLabel}. Network and inventory figures are current snapshots. Order value excludes cancelled orders; payment collection is not tracked.</div>
        {error && <div className="dashboard-error" role="alert"><span>{error}</span><button type="button" onClick={() => setRefreshKey((current) => current + 1)}>Retry</button></div>}
        {loading && <div className="dashboard-loading" role="status">{overview ? "Refreshing business data…" : "Loading business overview…"}</div>}

        <section className="dashboard-metrics" aria-label="Business summary">
          <MetricCard label="Order value" value={formatMoney(orderValue.total)} detail={`${formatMoney(orderValue.distributor)} direct · ${formatMoney(orderValue.shop)} shop`} comparison={{ current: orderValue.total, previous: comparison.orderValue?.total ?? 0 }} comparisonLabel={comparisonLabel} icon={CircleDollarSign} tone="teal" />
          <MetricCard label="Orders" value={counts.orders.total.toLocaleString("en-IN")} detail={`${counts.orders.pending} awaiting action`} comparison={{ current: counts.orders.total, previous: comparison.orders }} comparisonLabel={comparisonLabel} icon={ShoppingBag} tone="blue" />
          <MetricCard label="Distributors" value={counts.distributors.total.toLocaleString("en-IN")} detail={`${counts.distributors.active} active · ${counts.distributors.blocked} blocked`} icon={Store} tone="amber" />
          <MetricCard label="Shops & sales team" value={`${counts.shops.total} / ${counts.salesPeople.total}`} detail={`${counts.shops.active} active shops · ${counts.salesPeople.active} active reps`} icon={Users} tone="coral" />
          <MetricCard label="Product catalog" value={counts.products.total.toLocaleString("en-IN")} detail={`${counts.products.active} active · ${counts.products.categories} categories`} icon={Package} tone="green" />
          <MetricCard label="Units in stock" value={counts.inventory.stockUnits.toLocaleString("en-IN")} detail={`${counts.inventory.lowStockVariants} low · ${counts.inventory.outOfStockVariants} out of stock variants`} icon={Boxes} tone="slate" />
        </section>

        <section className="dashboard-panel dashboard-trend-panel">
          <PanelHeading title="Order value over time" caption={`${rangeLabel} · compared with ${comparisonLabel}`} />
          <div className="dashboard-chart dashboard-chart-trend">
            {hasOrders ? <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={overview.trend} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="distributorValueFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#246b8e" stopOpacity={0.22} /><stop offset="100%" stopColor="#246b8e" stopOpacity={0.01} /></linearGradient>
                  <linearGradient id="shopValueFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2a9d8f" stopOpacity={0.18} /><stop offset="100%" stopColor="#2a9d8f" stopOpacity={0.01} /></linearGradient>
                </defs>
                <CartesianGrid stroke="#e9edf0" vertical={false} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: "#71808b", fontSize: 11 }} minTickGap={16} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: "#71808b", fontSize: 11 }} tickFormatter={formatCompactMoney} width={68} />
                <Tooltip formatter={(value, name) => [formatMoney(value), name === "distributorValue" ? "Distributor purchases" : "Shop orders"]} contentStyle={tooltipStyle} />
                <Legend formatter={(value) => value === "distributorValue" ? "Distributor purchases" : "Shop orders"} />
                <Area type="monotone" dataKey="distributorValue" name="distributorValue" stroke="#246b8e" strokeWidth={2.5} fill="url(#distributorValueFill)" connectNulls />
                <Area type="monotone" dataKey="shopValue" name="shopValue" stroke="#2a9d8f" strokeWidth={2.5} fill="url(#shopValueFill)" connectNulls />
              </AreaChart>
            </ResponsiveContainer> : <EmptyChart message={`No order activity for ${rangeLabel.toLowerCase()}. Try a longer date range.`} />}
          </div>
        </section>

        <div className="dashboard-chart-grid">
          <section className="dashboard-panel">
            <PanelHeading title="Distributor reach by state" caption={`${counts.states} states · ${counts.districts} districts`} />
            <div className="dashboard-chart dashboard-chart-donut">
              {stateData.length ? <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stateData} dataKey="distributors" nameKey="name" innerRadius="55%" outerRadius="78%" paddingAngle={2} stroke="none">
                    {stateData.map((state, index) => <Cell key={state.id} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} distributors`, "Network"]} contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer> : <EmptyChart message="Distributor locations will appear here." />}
            </div>
            <div className="dashboard-chart-legend">
              {stateData.slice(0, 6).map((state, index) => <div key={state.id}><i style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} /><span>{state.name}</span><strong>{state.distributors}</strong></div>)}
              {stateData.length > 6 && <small>Additional states are grouped in the chart.</small>}
            </div>
          </section>

          <section className="dashboard-panel">
            <PanelHeading title="Order status" caption={`${counts.orders.total} orders · ${rangeLabel.toLowerCase()}`} />
            <div className="dashboard-chart dashboard-chart-donut">
              {hasOrders ? <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={overview.orderStatuses.filter((item) => item.count > 0)} dataKey="count" nameKey="status" innerRadius="55%" outerRadius="78%" paddingAngle={2} stroke="none">
                    {overview.orderStatuses.filter((item) => item.count > 0).map((item) => <Cell key={item.status} fill={STATUS_COLORS[item.status]} />)}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, statusLabel(name)]} contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer> : <EmptyChart message="Order status breakdown will appear here." />}
            </div>
            <div className="dashboard-chart-legend">
              {overview?.orderStatuses.filter((item) => item.count > 0).map((item) => <div key={item.status}><i style={{ backgroundColor: STATUS_COLORS[item.status] }} /><span>{statusLabel(item.status)}</span><strong>{item.count}</strong></div>)}
            </div>
          </section>
        </div>

        <section className="dashboard-panel dashboard-district-ranking-panel">
          <div className="dashboard-district-heading">
            <PanelHeading title="Top ordering districts" caption={`Direct and shop order value · ${rangeLabel.toLowerCase()}`} />
            <label className="dashboard-district-select-label">Inspect district
              <select value={selectedDistrictId} onChange={(event) => setSelectedDistrictId(event.target.value)} aria-label="Inspect district network">
                {!districtOptions.length && <option value="">No districts</option>}
                {districtOptions.map((district) => <option key={district.id} value={district.id}>{district.name} · {district.state}</option>)}
              </select>
            </label>
          </div>
          <div className="dashboard-district-chart">
            {orderingDistricts.length ? <ResponsiveContainer width="100%" height="100%">
              <BarChart data={orderingDistricts} layout="vertical" margin={{ top: 8, right: 22, left: 4, bottom: 0 }} onClick={(event) => {
                const districtId = event?.activePayload?.[0]?.payload?.id;
                if (districtId) setSelectedDistrictId(districtId);
              }}>
                <CartesianGrid stroke="#e9edf0" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: "#71808b", fontSize: 10 }} tickFormatter={formatCompactMoney} />
                <YAxis type="category" dataKey="name" width={116} axisLine={false} tickLine={false} tick={{ fill: "#485663", fontSize: 10 }} tickFormatter={(value) => value.length > 18 ? `${value.slice(0, 17)}…` : value} />
                <Tooltip formatter={(value) => [formatMoney(value), "Order value"]} labelFormatter={(label, items) => items?.[0]?.payload?.state ? `${label}, ${items[0].payload.state}` : label} contentStyle={tooltipStyle} />
                <Bar dataKey="orderValue" name="Order value" fill="#246b8e" radius={[0, 3, 3, 0]} maxBarSize={18} cursor="pointer">
                  {orderingDistricts.map((district, index) => <Cell key={district.id} fill={index === 0 ? "#21877d" : "#246b8e"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer> : <EmptyChart message={`No district order activity for ${rangeLabel.toLowerCase()}. Choose a district below to inspect its network.`} />}
          </div>
          {selectedDistrictId && <DistrictNetworkDetail key={`${selectedDistrictId}-${period}`} districtId={selectedDistrictId} range={period} />}
        </section>

        <div className="dashboard-chart-grid dashboard-ranking-grid">
          <section className="dashboard-panel">
            <PanelHeading title="Highest purchasing distributors" caption={`Direct order value · ${rangeLabel.toLowerCase()}`} />
            {overview?.topDistributors?.length ? <div className="dashboard-ranking-list">
              {overview.topDistributors.map((item, index) => <div className="dashboard-ranking-row" key={item.id}>
                <span className="dashboard-rank">{String(index + 1).padStart(2, "0")}</span>
                <div className="dashboard-ranking-main"><strong>{item.name}</strong><small>{item.state} · {item.orders} orders</small><div className="dashboard-rank-track"><i style={{ width: `${Math.max(6, (item.value / (overview.topDistributors[0].value || 1)) * 100)}%` }} /></div></div>
                <b>{formatCompactMoney(item.value)}</b>
              </div>)}
            </div> : <EmptyChart message="Purchasing rankings will appear when distributors place orders." />}
          </section>

          <section className="dashboard-panel">
            <PanelHeading title="Sales team performance" caption={`Shop order value · ${rangeLabel.toLowerCase()}`} />
            {overview?.topSalesPeople?.length ? <div className="dashboard-ranking-list">
              {overview.topSalesPeople.map((item, index) => <div className="dashboard-ranking-row" key={item.id}>
                <span className="dashboard-rank dashboard-rank-warm">{String(index + 1).padStart(2, "0")}</span>
                <div className="dashboard-ranking-main"><strong>{item.name}</strong><small>{item.distributor} · {item.orders} orders</small><div className="dashboard-rank-track dashboard-rank-track-warm"><i style={{ width: `${Math.max(6, (item.value / (overview.topSalesPeople[0].value || 1)) * 100)}%` }} /></div></div>
                <b>{formatCompactMoney(item.value)}</b>
              </div>)}
            </div> : <EmptyChart message="Sales team rankings will appear when shop orders are recorded." />}
          </section>
        </div>

        <div className="dashboard-product-grid">
          <section className="dashboard-panel">
            <PanelHeading title="Highest-demand products" caption={`Units sold vs current stock · ${rangeLabel.toLowerCase()}`} />
            <div className="dashboard-product-chart">
              {overview?.highestDemandProducts?.length ? <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview.highestDemandProducts} layout="vertical" margin={{ top: 8, right: 12, left: 4, bottom: 0 }} barGap={3}>
                  <CartesianGrid stroke="#e9edf0" horizontal={false} />
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: "#71808b", fontSize: 10 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={116} axisLine={false} tickLine={false} tick={{ fill: "#485663", fontSize: 10 }} tickFormatter={(value) => value.length > 18 ? `${value.slice(0, 17)}…` : value} />
                  <Tooltip formatter={(value, name) => [`${value} units`, name === "unitsSold" ? "Sold in period" : "Current stock"]} labelFormatter={(label) => label} contentStyle={tooltipStyle} />
                  <Legend formatter={(value) => value === "unitsSold" ? "Units sold" : "Stock now"} />
                  <Bar dataKey="unitsSold" name="unitsSold" fill="#246b8e" radius={[0, 3, 3, 0]} maxBarSize={13} />
                  <Bar dataKey="stockUnits" name="stockUnits" fill="#d49a3a" radius={[0, 3, 3, 0]} maxBarSize={13} />
                </BarChart>
              </ResponsiveContainer> : <EmptyChart message={`No product sales in ${rangeLabel.toLowerCase()}. Try a longer date range.`} />}
            </div>
            {overview?.highestDemandProducts?.length > 0 && <div className="dashboard-product-ranking">
              {overview.highestDemandProducts.slice(0, 5).map((product, index) => <div key={product.id}><span>{String(index + 1).padStart(2, "0")}</span><strong>{product.name}</strong><small>{product.orders} orders · {formatCompactMoney(product.orderValue)}</small></div>)}
            </div>}
          </section>

          <section className="dashboard-panel dashboard-stock-panel">
            <PanelHeading title="Low-stock watchlist" caption="Active products · low ≤5 units per size · out = 0" />
            {overview?.lowStockProducts?.length ? <DataTable headers={["Product", "Category", "Stock", "At-risk sizes"]} wrapStyle={{ border: 0, borderRadius: 0 }} tableStyle={{ minWidth: 0 }}>
              {overview.lowStockProducts.map((product) => <tr key={product.id}>
                <td><strong>{product.name}</strong></td>
                <td>{product.category}</td>
                <td><span className={product.stockUnits === 0 ? "dashboard-stock-zero" : "dashboard-stock-total"}>{product.stockUnits} units</span></td>
                <td><div className="dashboard-risk-sizes">{product.variants.map((variant) => <span key={`${variant.size}-${variant.stock}`} className={variant.stock === 0 ? "is-out" : "is-low"}>{variant.size}: {variant.stock === 0 ? "Out" : variant.stock}</span>)}</div></td>
              </tr>)}
            </DataTable> : <EmptyChart message="No active product sizes are at or below the low-stock threshold." />}
          </section>
        </div>

        <section className="dashboard-panel dashboard-recent-panel">
          <PanelHeading title="Recent orders" caption={`Latest activity · ${rangeLabel.toLowerCase()}`} />
          {overview?.recentOrders?.length ? <DataTable headers={["Order", "Channel / buyer", "Base", "Discount", "GST", "Total payable", "Status", "Placed"]} wrapStyle={{ border: 0, borderRadius: 0 }} tableStyle={{ minWidth: 0 }}>
            {overview.recentOrders.map((order) => <tr key={`${order.channel}-${order.id}`}>
              <td><strong>#{order.number}</strong></td>
              <td><strong>{order.buyer}</strong><small>{order.channel}{order.salesperson ? ` · ${order.salesperson}` : ""}</small></td>
              <td>{order.baseAmount == null ? "-" : formatMoney(order.baseAmount)}</td>
              <td>{order.discountPercent == null ? "-" : `${order.discountPercent}% · -${formatMoney(order.discountAmount)}`}</td>
              <td>{order.gstAmount == null ? "Not recorded" : `${order.gstPercent}% · ${formatMoney(order.gstAmount)}`}</td>
              <td><strong>{formatMoney(order.totalAmount)}</strong></td>
              <td><span className={`dashboard-order-status status-${order.status}`}>{statusLabel(order.status)}</span></td>
              <td>{formatDate(order.createdAt)}</td>
            </tr>)}
          </DataTable> : <EmptyChart message="New distributor and shop orders will appear here." />}
        </section>

        <footer className="dashboard-footnote">
          <span><MapPinned size={14} aria-hidden="true" /> {counts.states} states · {counts.districts} districts</span>
          <span><ArrowUpRight size={14} aria-hidden="true" /> Distributor orders and shop orders are reported separately to avoid mixing sales channels.</span>
          {counts.inventory.lowStockVariants + counts.inventory.outOfStockVariants > 0 && <span className="dashboard-stock-alert"><ArrowDownRight size={14} aria-hidden="true" /> {counts.inventory.lowStockVariants + counts.inventory.outOfStockVariants} variants need stock attention</span>}
        </footer>
      </main>
    </AppShell>
  );
}

function PermissionOverview({ admin }) {
  const grantedLabels = FALLBACK_PERMISSIONS.filter((permission) => admin?.permissions?.includes(permission.key)).map((permission) => permission.label);
  return <AppShell>
    <div className="permission-overview">
      <h1>Welcome, {admin?.name}</h1>
      <p>Here are the features available to your account. Contact the Super Admin if you need access to more.</p>
      <div className="permission-overview-grid">
        <section><span>Role</span><strong>Sub Admin</strong></section>
        <section><span>Account status</span><strong>{admin?.status === "active" ? "Active" : "Suspended"}</strong></section>
        <section><span>Granted features</span><strong>{grantedLabels.length}</strong></section>
      </div>
      <section className="permission-overview-access"><h2>Your access</h2>{grantedLabels.length ? <ul>{grantedLabels.map((label) => <li key={label}>{label}</li>)}</ul> : <p>No features are assigned to your account yet.</p>}</section>
    </div>
  </AppShell>;
}

function MetricCard({ label, value, detail, comparison, comparisonLabel, icon: Icon, tone }) {
  return <article className={`dashboard-metric tone-${tone}`}>
    <div className="dashboard-metric-top"><span>{label}</span><i><Icon size={18} strokeWidth={1.8} aria-hidden="true" /></i></div>
    <strong className="dashboard-metric-value">{value}</strong>
    {comparison && <ComparisonBadge current={comparison.current} previous={comparison.previous} label={comparisonLabel} />}
    <small>{detail}</small>
  </article>;
}

function ComparisonBadge({ current, previous, label }) {
  const isUp = current >= previous;
  const percent = previous === 0 ? null : Math.round(Math.abs((current - previous) / previous) * 100);
  const Icon = isUp ? ArrowUpRight : ArrowDownRight;
  return <span className={`dashboard-comparison ${isUp ? "comparison-up" : "comparison-down"}`} title={`Compared with ${label}`}>
    <Icon size={13} aria-hidden="true" />
    <strong>{percent === null ? (current > 0 ? "New" : "0%") : `${percent}%`}</strong>
    <small>vs previous</small>
  </span>;
}

function PanelHeading({ title, caption }) {
  return <div className="dashboard-panel-heading"><div><h2>{title}</h2><p>{caption}</p></div></div>;
}

function EmptyChart({ message }) {
  return <div className="dashboard-empty-chart"><span><Activity size={18} aria-hidden="true" /></span><p>{message}</p></div>;
}

const tooltipStyle = { border: "1px solid #d9dee3", borderRadius: 4, background: "#fff", fontSize: 12 };
